import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangedContext, CollectionChangingAction, ModelBase, ObservableCollection, Weaver } from '@skylab-shared/core';
import { CalculationMethod, DiscountKind, DocumentItemModel, ExchangeRateModel, VatTableItemModel } from '@skylab-shared/dataModel';
import { UnboxCollection } from '@skylab-shared/types';
import { Calc, MidpointRounding, withFlag } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentItemForDocument } from '@skylab/calculations/document';
import { DocumentItem, DocumentItemCalculator, DocumentItemChanges, DocumentItemChangesProperties, DocumentItemGrossCalculator, DocumentItemNetCalculator, MinimalDocumentItem } from '@skylab/calculations/document-item';
import { applyDiscount, applyTax, removeDisocunt, removeTax } from '../../common/calculators/item-calculation';
import { ContextKeys } from '../../common/context-keys';
import { propertyChangedFilter } from '../../common/property-changed/property-changed-filter';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery, CurrencyItem } from '../../currency/queries/currency.query-base';
import { collectionChangedFilter } from '../../utils/collection-changed-filter';
import { VatRateItem } from '../../vat/queries/vat-rate-item';
import { DocumentItemMarginCalculator, MarginItem } from '../algorithms/item/item-margin-calculator';
import { DocumentItemRecalculatedJoinPoint, DocumentItemRecalculatedJoinPointContext, DocumentRecalculatedJoinPoint, DocumentRecalculatedJoinPointContext } from '../utils/recalculate-document-join-point';

export class DocumentItemCalculatorModel extends ModelBase {
    public ordinalNumber!: number;
    public price_grossAfterDiscount!: number;
    public price_grossBeforeDiscount!: number;
    public price_netAfterDiscount!: number;
    public price_netBeforeDiscount!: number;
    public value_grossAfterDiscount!: number;
    public value_grossBeforeDiscount!: number;
    public value_netAfterDiscount!: number;
    public value_netBeforeDiscount!: number;
    public value_vatAfterDiscount!: number;
    public value_vatBeforeDiscount!: number;
    public vatRateId: any; //TODO: póki cały subiekt nie korzysta z externali to musi być to any, bo się sypie w tym generyku
    public discountValue!: number;
    public quantity!: number;
    public discountKind!: DiscountKind;
}

export interface DocumentCalculatorModelPart<TItemModel extends DocumentItemCalculatorModel> extends ModelBase {
    calculationMethod: CalculationMethod;
    currencyId: number;
    exchangeRate: ExchangeRateModel;
    grossValue: number;
    issuedByVatPayer: boolean;
    items: ObservableCollection<TItemModel>;
    netValue: number;
    vatTableItems: ObservableCollection<VatTableItemModel>;
    vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;
}

export abstract class DocumentCalculatorBoPart<TDocumentModelPart extends DocumentCalculatorModelPart<TItemModel>, TItemModel extends DocumentItemCalculatorModel> extends BusinessObjectPart<TDocumentModelPart> {
    public get itemPropertiesThatTriggerRecalculation(): (keyof TItemModel)[] {
        return  [
            'vatRateId',
            'quantity',
            'discountValue',
            'discountKind',
            'price_grossAfterDiscount',
            'price_grossBeforeDiscount',
            'price_netAfterDiscount',
            'price_netBeforeDiscount',
            'value_grossAfterDiscount',
            'value_grossBeforeDiscount',
            'value_netAfterDiscount',
            'value_netBeforeDiscount',
            'value_vatAfterDiscount',
            'value_vatBeforeDiscount'
        ];
    }

    public get documentPropertiesThatTriggerRecalculation(): (keyof TDocumentModelPart)[] {
        return  [
            'calculationMethod',
            'currencyId',
            'items'
        ];
    }

    public constructor(weaver: Weaver, private readonly itemModel?: any) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<TDocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);

        await Promise.all([
            this.bo.localContext.preload(CurrencyCollectionQuery),
            this.bo.localContext.preload(CompanyParametersQuery),
            this.bo.localContext.preload(ContextKeys.systemCurrency)
        ]);
        this.registerCoreFeatures();
    }

    private registerCoreFeatures(): void {
        // Przeliczenie pozycji, na której nastąpiła zmiana oraz przeliczenie dokumentu.
        this.coreFeature.propertyChanged.listen<DocumentItemCalculatorModel>(propertyChangedFilter<DocumentItemCalculatorModel>(
            this.itemModel || DocumentItemModel,
            this.itemPropertiesThatTriggerRecalculation as (keyof DocumentItemCalculatorModel)[],
            ctx => {
                withFlag(() => {

                    withFlag(() => {
                        if (!isDocumentItemReadyForCalculation(this.bo.model, ctx.model)) return;
                        this.recalculateTItemModelAndRewrite(ctx.model as TItemModel, this.bo.model.exchangeRate, ctx.propertyName as DocumentItemChangesProperties);
                    }, documentItemRecalculation(ctx.model));

                    if (!isDocumentReadyForCalculation(this.bo.model)) return;
                    this.recalculateDocumentModelAndRewrite(this.bo.model);
                }, documentRecalculation(this.bo.model));
            }
        ));

        // Przeliczenie wszystkich pozycji, po zmianach na dokumencie oraz przeliczenie dokumentu.
        this.coreFeature.propertyChanged.listen<TDocumentModelPart>(propertyChangedFilter(
            this.modelType,
            this.documentPropertiesThatTriggerRecalculation,
            ctx => {
                withFlag(() => {
                    if (!isDocumentReadyForCalculation(ctx.model)) return;

                    ctx.model.items.forEach(item => {
                        withFlag(() => {
                            this.recalculateTItemModelAndRewrite(item, this.bo.model.exchangeRate);
                        }, documentItemRecalculation(item));
                    });

                    this.recalculateDocumentModelAndRewrite(ctx.model);
                }, documentRecalculation(ctx.model));
            }
        ));

        // Przeliczenie dokumentu po zmianie kursu waluty.
        this.coreFeature.propertyChanged.listen<ExchangeRateModel>(propertyChangedFilter(
            ExchangeRateModel,
            ['rate', 'units'],
            () => {
                withFlag(() => {
                    if (!isDocumentReadyForCalculation(this.bo.model)) return;

                    this.recalculateDocumentModelAndRewrite(this.bo.model);
                }, documentRecalculation(this.bo.model));
            }
        ));

        // Przeliczenie nowo dodanych pozycji oraz przeliczenie dokumentu.
        this.coreFeature.collectionChanged.listen<TDocumentModelPart>(ctx => onItemsCollectionChanged(ctx as unknown as CollectionChangedContext<TDocumentModelPart, TItemModel>));
        const onItemsCollectionChanged = collectionChangedFilter<TDocumentModelPart>(
            this.modelType,
            'items',
            ctx => {
                withFlag(() => {
                    if (!isDocumentReadyForCalculation(this.bo.model)) return;

                    if (ctx.action === CollectionChangingAction.Remove || ctx.action === CollectionChangingAction.Add) {
                        ctx.newItems.forEach(item => {
                            withFlag(() => {
                                this.recalculateTItemModelAndRewrite(item, this.bo.model.exchangeRate);
                            }, documentItemRecalculation(item));
                        });

                        this.recalculateDocumentModelAndRewrite(ctx.model as TDocumentModelPart);
                    }
                }, documentRecalculation(ctx.model));
            }
        );
    }

    protected recalculateTItemModelAndRewrite(itemModel: TItemModel, exchangeRate: ExchangeRateModel, updatedProperty?: DocumentItemChangesProperties): void {
        const { precision } = this.getCurrencyItem(this.bo.model.currencyId);

        const beforeDiscount = this.recalculateTItemModel_beforeDiscount(itemModel, exchangeRate, precision, updatedProperty);
        rewriteToTItemModel_beforeDiscount(itemModel, beforeDiscount);

        const afterDiscount = this.recalculateTItemModel_afterDiscount(itemModel, exchangeRate, precision);
        rewriteToTItemModel_afterDiscount(itemModel, afterDiscount);

        this.weaver.weave(new DocumentItemRecalculatedJoinPoint(new DocumentItemRecalculatedJoinPointContext(itemModel)));
    }

    private recalculateTItemModel_beforeDiscount(
        itemModel: TItemModel,
        exchangeRate: ExchangeRateModel,
        precision: number,
        updatedProperty?: DocumentItemChangesProperties
    ): DocumentItem {
        const { rate } = this.getVatRateItem(itemModel.vatRateId);

        const inForeignCurrency = exchangeRate !== null;
        // olekm: newUpdatedProperty - sami symulujemy zmianę innego pola w celu utrzymania poprawności obliczeń
        const [item_beforeDiscount, newUpdatedProperty] = this.convertDocumentItemToItem_beforeDiscount(itemModel, {
            rate,
            exchangeRate: inForeignCurrency ? {
                rate: exchangeRate.rate,
                units: exchangeRate.units
            } : { rate: 1, units: 1 },
            calculationMethod: this.bo.model.calculationMethod,
            precision
        }, updatedProperty);

        const calculator = getDocumentItemCalculator(this.bo.model);
        const calculationContext = { precision };

        return calculator.recalculate(item_beforeDiscount, calculationContext, newUpdatedProperty);
    }

    private recalculateTItemModel_afterDiscount(
        itemModel: TItemModel,
        exchangeRate: ExchangeRateModel,
        precision: number
    ): DocumentItem {
        const { rate } = this.getVatRateItem(itemModel.vatRateId);

        const inForeignCurrency = exchangeRate !== null;
        const item_afterDiscount = this.convertDocumentItemToItem_afterDiscount(itemModel, {
            rate,
            exchangeRate: inForeignCurrency ? {
                rate: exchangeRate.rate,
                units: exchangeRate.units
            } : { rate: 1, units: 1 },
            calculationMethod: this.bo.model.calculationMethod,
            precision
        });

        const calculator = getDocumentItemCalculator(this.bo.model);
        const calculationContext = { precision };

        return calculator.recalculate(item_afterDiscount, calculationContext);
    }

    public recalculateDocumentModelAndRewrite(documentModel: TDocumentModelPart): void {
        const recalculatedDocument = this.recalculateDocumentModel(documentModel);
        this.rewriteToDocumentModel(documentModel, recalculatedDocument);

        this.weaver.weave(new DocumentRecalculatedJoinPoint(new DocumentRecalculatedJoinPointContext(this.bo)));
    }

    protected rewriteToDocumentModel(documentModel: TDocumentModelPart, document: { inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }): void {
        documentModel.vatTableItems.clear();
        documentModel.vatTableItemsInBaseCurrency.clear();

        document.inDocumentCurrency.vatTableItems.map(item => {
            const model = new VatTableItemModel();
            documentModel.vatTableItems.push(model);
            this.rewriteVatTableItemToVatTableItemModel(item, model);
        });

        if (document.inBaseCurrency) {
            document.inBaseCurrency.vatTableItems.map(item => {
                const model = new VatTableItemModel();
                documentModel.vatTableItemsInBaseCurrency.push(model);
                this.rewriteVatTableItemToVatTableItemModel(item, model);
            });
        }

        documentModel.netValue = document.inDocumentCurrency.netValue;
        documentModel.grossValue = document.inDocumentCurrency.grossValue;
    }

    protected rewriteVatTableItemToVatTableItemModel(item: UnboxCollection<CalculatedDocument['vatTableItems']>, model: VatTableItemModel): void {
        model.netValue = item.netValue;
        model.vatValue = item.vatValue;
        model.grossValue = item.grossValue;
        model.vatRateId = item.vatRateId;
    }

    protected convertDocumentItemToItem(itemModel: TItemModel, { rate, exchangeRate }: DocumentItemParameters): MinimalDocumentItem {
        return {
            no: itemModel.ordinalNumber,
            vatRateId: itemModel.vatRateId,
            vatRate: rate,
            quantity: itemModel.quantity,
            netPrice: itemModel.price_netAfterDiscount,
            grossPrice: itemModel.price_grossAfterDiscount,
            netValue: itemModel.value_netAfterDiscount,
            grossValue: itemModel.value_grossAfterDiscount,
            vatValue: itemModel.value_vatAfterDiscount
        };
    }

    protected getItemModelToConvert_afterDiscount(itemModel: TItemModel, { rate, exchangeRate }: DocumentItemParameters): DocumentItem {
        return {
            no: itemModel.ordinalNumber,
            vatRateId: itemModel.vatRateId,
            vatRate: rate,
            quantity: itemModel.quantity,
            netPrice: itemModel.price_netAfterDiscount,
            grossPrice: itemModel.price_grossAfterDiscount,
            netValue: itemModel.value_netAfterDiscount,
            grossValue: itemModel.value_grossAfterDiscount
        };
    }

    private convertDocumentItemToItem_afterDiscount(itemModel: TItemModel, { rate, exchangeRate, calculationMethod, precision }: DocumentItemParameters): MinimalDocumentItem {
        const item = this.getItemModelToConvert_afterDiscount(itemModel, {rate, exchangeRate, calculationMethod, precision});

        const { discountKind: discountKind, discountValue: discount } = itemModel;

        if (calculationMethod === CalculationMethod.Net) {
            const netPrice = applyDiscount(itemModel.price_netBeforeDiscount, discountKind, discount);
            item.netPrice = Calc.round(netPrice, precision, MidpointRounding.AwayFromZero);
        } else if (calculationMethod === CalculationMethod.Gross || calculationMethod === CalculationMethod.Margin) {
            const grossPrice = applyDiscount(itemModel.price_grossBeforeDiscount, discountKind, discount);
            item.grossPrice = Calc.round(grossPrice, precision, MidpointRounding.AwayFromZero);
        }

        return item as MinimalDocumentItem;
    }

    protected getItemModelToConvert_beforeDiscount(itemModel: TItemModel, { rate, exchangeRate }: DocumentItemParameters): DocumentItem {
        return {
            no: itemModel.ordinalNumber,
            vatRateId: itemModel.vatRateId,
            vatRate: rate,
            quantity: itemModel.quantity,
            netPrice: itemModel.price_netBeforeDiscount,
            grossPrice: itemModel.price_grossBeforeDiscount,
            netValue: itemModel.value_netBeforeDiscount,
            grossValue: itemModel.value_grossBeforeDiscount
        };
    }

    private convertDocumentItemToItem_beforeDiscount(itemModel: TItemModel, { rate, exchangeRate, calculationMethod, precision }: DocumentItemParameters, documentUpdatedProperty?: DocumentItemChangesProperties): [MinimalDocumentItem, string]  {
        const item = this.getItemModelToConvert_beforeDiscount(itemModel, {rate, exchangeRate, calculationMethod, precision});

        const updatedProperty = documentUpdatedProperty as DocumentItemChangesProperties;
        if (!DocumentItemChanges.afterDiscountChanged(updatedProperty)) {
            return [item as MinimalDocumentItem, updatedProperty];
        }

        const { discountKind: discountKind, discountValue: discount } = itemModel;
        let newUpdatedproperty = '';

        // olekm: zmiany po rabacie sprowadzamy do zmiany podstawowej wartości przed rabatem, unikając zaookrągleń po drodze - ułatwia to zachowanie wpisanej wartości

        if (calculationMethod === CalculationMethod.Net) {
            if (DocumentItemChanges.priceChanged(updatedProperty)) {
                if (DocumentItemChanges.netChanged(updatedProperty)) {
                    const netPrice = removeDisocunt(itemModel.price_netAfterDiscount, discountKind, discount);
                    item.netPrice = Calc.round(netPrice, precision, MidpointRounding.AwayFromZero);
                } else if (DocumentItemChanges.grossChanged(updatedProperty)) {
                    const netAfterDiscount = removeTax(itemModel.price_grossAfterDiscount, rate);
                    const netBeforeDiscount = removeDisocunt(netAfterDiscount, discountKind, discount);
                    item.netPrice = Calc.round(netBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                }
            } else if (DocumentItemChanges.valueChanged(updatedProperty)) {
                if (DocumentItemChanges.netChanged(updatedProperty)) {
                    const netPriceAfterDiscount = itemModel.quantity === 0 ? 0 : (itemModel.value_netAfterDiscount / itemModel.quantity);
                    const netPriceBeforeDiscount = removeDisocunt(netPriceAfterDiscount, discountKind, discount);
                    item.netPrice = Calc.round(netPriceBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                } else if (DocumentItemChanges.grossChanged(updatedProperty)) {
                    const netValueAfterDiscount = removeTax(itemModel.value_grossAfterDiscount, rate);
                    const netPriceAfterDiscount = itemModel.quantity === 0 ? 0 : (netValueAfterDiscount / itemModel.quantity);
                    const netPriceBeforeDiscount = removeDisocunt(netPriceAfterDiscount, discountKind, discount);
                    item.netPrice = Calc.round(netPriceBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                }
            }
            newUpdatedproperty = 'netPrice';
        } else if (calculationMethod === CalculationMethod.Gross || calculationMethod === CalculationMethod.Margin) {
            if (DocumentItemChanges.priceChanged(updatedProperty)) {
                if (DocumentItemChanges.netChanged(updatedProperty)) {
                    const grossAfterDiscount = applyTax(itemModel.price_netAfterDiscount, rate);
                    const grossBeforeDiscount = removeDisocunt(grossAfterDiscount, discountKind, discount);
                    item.grossPrice = Calc.round(grossBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                } else if (DocumentItemChanges.grossChanged(updatedProperty)) {
                    const grossPrice = removeDisocunt(itemModel.price_grossAfterDiscount, discountKind, discount);
                    item.grossPrice = Calc.round(grossPrice, precision, MidpointRounding.AwayFromZero);
                }
            } else if (DocumentItemChanges.valueChanged(updatedProperty)) {
                if (DocumentItemChanges.netChanged(updatedProperty)) {
                    const grossValueAfterDiscount = applyTax(itemModel.value_netAfterDiscount, rate);
                    const grossPriceAfterDiscount = itemModel.quantity === 0 ? 0 : (grossValueAfterDiscount / itemModel.quantity);
                    const grossPriceBeforeDiscount = removeDisocunt(grossPriceAfterDiscount, discountKind, discount);
                    item.grossPrice = Calc.round(grossPriceBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                } else if (DocumentItemChanges.grossChanged(updatedProperty)) {
                    const grossriceAfterDiscount = itemModel.quantity === 0 ? 0 : (itemModel.value_grossAfterDiscount / itemModel.quantity);
                    const grossPriceBeforeDiscount = removeDisocunt(grossriceAfterDiscount, discountKind, discount);
                    item.grossPrice = Calc.round(grossPriceBeforeDiscount, precision, MidpointRounding.AwayFromZero);
                }
            }
            newUpdatedproperty = 'grossPrice';
        }

        return [item as MinimalDocumentItem, newUpdatedproperty];
    }

    protected abstract recalculateDocumentModel(documentModel: TDocumentModelPart): Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }>;
    protected abstract getVatRateItem(id: number | string): VatRateItem;
    protected getCurrencyItem(id: number | string): CurrencyItem {
        const currency = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId });
        if (!currency) {
            throw new Error(`Currency not found for id: ${this.bo.model.currencyId}`);
        }

        return currency;
    };

}

function rewriteToTItemModel_beforeDiscount(itemModel: DocumentItemCalculatorModel, item: DocumentItem & MarginItem): void {
    itemModel.price_netBeforeDiscount = item.netPrice!;
    itemModel.price_grossBeforeDiscount = item.grossPrice!;
    itemModel.value_netBeforeDiscount = item.netValue!;
    itemModel.value_grossBeforeDiscount = item.grossValue!;
    itemModel.value_vatBeforeDiscount = item.vatValue!;
}

function rewriteToTItemModel_afterDiscount(itemModel: DocumentItemCalculatorModel, item: DocumentItem & MarginItem): void {
    itemModel.price_netAfterDiscount = item.netPrice!;
    itemModel.price_grossAfterDiscount = item.grossPrice!;
    itemModel.value_netAfterDiscount = item.netValue!;
    itemModel.value_grossAfterDiscount = item.grossValue!;
    itemModel.value_vatAfterDiscount = item.vatValue!;
}

export function isDocumentItemReadyForCalculation(model: DocumentCalculatorModelPart<DocumentItemCalculatorModel>, itemModel: DocumentItemCalculatorModel): boolean {
    if (model.calculationMethod === CalculationMethod.Manual) return false;

    return (model.currencyId !== 0) && (model.currencyId !== null)
        && (itemModel.vatRateId !== 0) && (itemModel.vatRateId !== null)
        && (model.calculationMethod !== null);
}

export function isDocumentReadyForCalculation(model: DocumentCalculatorModelPart<DocumentItemCalculatorModel>): boolean {
    if (model.calculationMethod === CalculationMethod.Manual) return false;

    const itemsReadyForRecalculation = model.items
        .reduce((isReadyForRecalculation, item) => isReadyForRecalculation && isDocumentItemReadyForCalculation(item), true);
    return (model.currencyId !== 0) && (model.currencyId !== null) && itemsReadyForRecalculation && model.calculationMethod !== null;

    function isDocumentItemReadyForCalculation(model: DocumentItemCalculatorModel): boolean {
        return (model.vatRateId !== 0) && (model.vatRateId !== null);
    }
}

export function documentRecalculation(model: DocumentCalculatorModelPart<DocumentItemCalculatorModel>): string {
    return `${model.sun}#document-recalculation`;
}

export function documentItemRecalculation(documentItem: DocumentItemCalculatorModel): string {
    return `${documentItem.sun}#document-item-recalculation`;
}

function getDocumentItemCalculator(document: { calculationMethod: CalculationMethod }): DocumentItemCalculator {
    switch (document.calculationMethod) {
        case CalculationMethod.Net: return new DocumentItemNetCalculator() as unknown as DocumentItemCalculator;
        case CalculationMethod.Gross: return new DocumentItemGrossCalculator() as unknown as DocumentItemCalculator;
        case CalculationMethod.Margin: return new DocumentItemMarginCalculator() as unknown as DocumentItemCalculator;
        default:
            throw new RangeError(`Unexpected calculation method.`);
    }
}

export function convertItemToBaseCurrency(item: DocumentItem, exchangeRate: { units: number; rate: number; }, precision: number ): DocumentItemForDocument {
    const rate = (exchangeRate.rate || 1) / (exchangeRate.units || 1);

    //olekm: idea jest taka, że nie przeliczamy pozycji w walucie, potrzebujemy jedynie uzyskać wartość w walucie
    const itemInCurrency = {
        ...item,
        netValue: Calc.round(item.netValue! * rate, precision, MidpointRounding.AwayFromZero),
        grossValue: Calc.round(item.grossValue! * rate, precision, MidpointRounding.AwayFromZero),
        netPrice: Calc.round(item.netPrice! * rate, precision, MidpointRounding.AwayFromZero),
        grossPrice: Calc.round(item.grossPrice! * rate, precision, MidpointRounding.AwayFromZero)
    };

    return itemInCurrency;
}

export interface DocumentItemParameters {
    readonly rate: number;
    readonly exchangeRate: {
        rate: number;
        units: number;
    };
    readonly calculationMethod: CalculationMethod;
    readonly precision: number;
}
